import {
    CertificateType,
    ConditionType,
    drivePowerOptions,
    ICertificate,
    loadCapacityOptions,
    vehicleLengthOptions,
} from "../../../models/certificate";
import { IIDDataSet } from "../../../models/crewMember";
import { CrewMemberImage } from "../../CrewMember/CrewMemberImage";
import coatOfArmsAt from "../../../images/coat_of_arms_at.png";
import { TranslateProps } from "../../../i18n/TranslateProps";
import { formatDate } from "../../../models/common";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "../../../store/configureStore";
import { Loader } from "semantic-ui-react";
import { IAdministratorParameters } from "../../../models/administratorSettings";
import "../ViewCertificateModal.css";
import { getAdministratorParameters } from "../../../api/administratorSettings";
import { useIntl } from "react-intl";
import { stringify } from "querystring";

type Props = {
    certificate: ICertificate;
    idDataSet: IIDDataSet;
    modifyable: boolean;
};

export const InternationalCertificateView = ({ certificate, idDataSet, modifyable }: Props) => {
    const [loading, setLoading] = useState(
        certificate.type === CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
    );
    const intl = useIntl();
    const [adminSettings, setAdminSettings] = useState<IAdministratorParameters>();
    const nationalities = useSelector((state: AppState) => state.nationalities.nationalities);
    const nationality = nationalities.find(c => c.value === idDataSet.nationality);
    const country =
        idDataSet.country >= 0 ? nationalities.find(c => c.value === idDataSet.country) : null;
    const isICS =
        certificate.type === CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS;

    const address = useMemo(() => {
        const displayZip =
            isICS && country ? `${country?.text}-${idDataSet.zipCode}` : idDataSet.zipCode;

        const result: string[] = [];
        if (!isICS && !!idDataSet.street) result.push(idDataSet.street);
        if (!!idDataSet.city && !!displayZip) result.push(displayZip + " " + idDataSet.city);
        else if (!!displayZip) result.push(displayZip);
        else if (!!idDataSet.city) result.push(idDataSet.city);
        return result.join(", ");
    }, [isICS, country, idDataSet.city, idDataSet.street, idDataSet.zipCode]);

    const getAdminSettings = useCallback(async () => {
        if (
            certificate.type ===
            CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS
        ) {
            const res = await getAdministratorParameters();
            setAdminSettings(res);
        }
        setLoading(false);
    }, [certificate.type, setLoading, setAdminSettings]);

    const getConditionsValue = () => {
        let result: string = "";
        let enumList: string[] = [];
        if (certificate.conditions && certificate.conditions.length > 0) {
            enumList = certificate.conditions.map(item =>
                intl.formatMessage({ id: ConditionType[item] })
            );
            result = enumList.join(", ");
            result = result.substring(0, 40);
            result =
                result.charAt(result.length - 1) === "," ? result.substring(result.length) : result;
        } else result = "--";
        return result;
    };

    useEffect(() => {
        getAdminSettings();
    }, [getAdminSettings]);

    return (
        <div className="certificate-front">
            {loading ? (
                <Loader active inline="centered" size="huge" />
            ) : (
                <>
                    <div className="front-left">
                        <p className="front-headline">
                            INTERNATIONAL CERTIFICATE FOR OPERATORS OF PLEASURE CRAFT
                        </p>
                        <p id="positions" style={{ paddingTop: "30px" }}>
                            1.&nbsp;
                            {idDataSet.nonLatinLastName
                                ? idDataSet.nonLatinLastName
                                : idDataSet.lastName}
                        </p>
                        <p id="positions">
                            2.&nbsp;
                            {idDataSet.nonLatinFirstName
                                ? idDataSet.nonLatinFirstName
                                : idDataSet.firstName}
                        </p>
                        <p id="positions">
                            3.&nbsp;
                            {`${formatDate(idDataSet.dateOfBirth)} - ${
                                idDataSet.nonLatinPlaceOfBirth
                                    ? idDataSet.nonLatinPlaceOfBirth
                                    : idDataSet.placeOfBirth
                            }`}
                        </p>
                        <p id="positions">4.&nbsp;{getStartDate(certificate, isICS)}</p>
                        <p id="positions">8.&nbsp;{address}</p>
                        <p id="positions">
                            9.&nbsp;
                            {nationality?.value === -1 ? "--" : (nationality?.text as string)}
                        </p>
                        <p id="positions">10.&nbsp;{certificate.validFor}</p>
                        <p id="positions">
                            11.&nbsp;{" "}
                            {isICS
                                ? combineLenghtLoadAndDriveValues(certificate)
                                : combineLoadAndDriveValues(certificate)}
                        </p>
                        <p id="positions">
                            12.&nbsp;
                            {certificate.endDate.includes("0001-") ||
                            certificate.endDate.includes("-1") ||
                            isICS
                                ? "--"
                                : formatDate(certificate.endDate)}
                        </p>
                        <p id="positions">
                            13.&nbsp;{isICS ? adminSettings?.issuingAuthority : "BMIMI"}
                        </p>
                        <p id="positions">14.&nbsp;{isICS ? adminSettings?.authorizedBy : "--"}</p>
                        <p style={{ paddingBottom: "20px" }}>
                            15. {isICS ? "--" : getConditionsValue()}
                        </p>
                    </div>
                    <div className="front-right">
                        <p className="front-headline">REPUBLIC OF AUSTRIA</p>
                        <img src={coatOfArmsAt} alt="coatOfArmsAT" id="coatOfArms"></img>
                        <div className="picture">
                            <div className="picture-frame" style={{ marginTop: "35px" }}>
                                <CrewMemberImage imageBase64={idDataSet.imageBase64} />
                            </div>
                        </div>
                        <p style={{ paddingTop: "20px" }}>
                            5.&nbsp;
                            {modifyable ? <em>Wird zugeteilt</em> : certificate.qualificationID}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

const getStartDate = (certificate: ICertificate, isICS: boolean) => {
    if (isICS) {
        const hasOutgoingDate =
            certificate.outgoingDate && !certificate.outgoingDate.startsWith("000");
        return hasOutgoingDate ? formatDate(certificate.outgoingDate) : <em>Wird zugeteilt</em>;
    } else {
        return formatDate(certificate.startDate);
    }
};

const combineLenghtLoadAndDriveValues = (certificate: ICertificate) => {
    const parts = ["<300 gross tonnage"];

    if (certificate.validFor.includes("M")) {
        parts.push(certificate.validFor.includes("M (3 nm)") ? "10 m (M)" : "< 24 m (M)");
    }
    if (certificate.validFor.includes("S")) {
        parts.push(certificate.validFor.includes("S (3 nm)") ? "10 m (S)" : "< 24 m (S)");
    }

    return parts.join(", ");
};

const combineLoadAndDriveValues = (certificate: ICertificate) => {
    const parts: string[] = [];
    if (certificate.vehicleLength > -1)
        parts.push(
            TranslateProps(vehicleLengthOptions).find(x => x.value === certificate.vehicleLength)
                ?.text as string
        );
    if (certificate.loadCapacity > -1) {
        const val = loadCapacityOptions.find(x => x.value === certificate.loadCapacity)
            ?.text as string;
        const split = val.split(" ");
        parts.push(split[0] + " " + certificate.carryingCapacityValue + " " + split[1]);
    }
    if (certificate.drivePower > -1) {
        const val = drivePowerOptions.find(x => x.value === certificate.drivePower)?.text as string;
        const split = val.split(" ");
        parts.push(split[0] + " " + certificate.drivePowerValue + " " + split[1]);
    }
    return parts.join(", ");
};
